import { readChat } from "@/api/chat";
import { getJobseekerMeetingByJobId } from "@/api/jobseeker";
import { axiosInstance } from "@/config/axios-wrapper";
import { V2Response } from "@/types/api";
import { Meeting } from "@/types/meeting";
import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";


interface Chat {
  id: string;
  unread_count: number;
  organization: {
    id: string;
    name: string;
    logo: string;
    logo_thumbnail_url: string;
    deleted?: boolean;
  };
  job: {
    deleted: boolean;
    id: string;
    alt_id: string;
    title: string;
    address: { line1: string; line2: string; city: string; zip_code: string; country: string };
    paused: boolean;
  };
  jobseeker: {
    id: string;
    full_name: string;
    job_title: string;
    photo: string;
    photo_thumbnail_url: string;
    bc_verified: boolean;
    deleted?: boolean;
  };
  last_message?: {
    id: string;
    sender: string;
    sender_id: string;
    type: string;
    text: string;
    files: Array<{ name: string; mimetype: string; url: string }>;
    created_at: string;
    updated_at: string;
  };
  ended: boolean;
  created_at: string;
  updated_at: string;
}

interface ChatState {
  list: Chat[];
  unread_list: Chat[];
  total: number;
  skip: number;
  unread: number;
  meeting: Meeting;
}

const initialState: ChatState = {
  list: [],
  unread_list: [],
  total: 0,
  skip: 0,
  unread: 0,
  meeting: null,
};

export const getChatMeeting = createAsyncThunk(
  "chat/getChatMeeting",
  async ({ jobseekerId, jobId }: { jobseekerId: string; jobId: string }) => {
    const data = await getJobseekerMeetingByJobId(jobseekerId);

    const meetings = data?.meetings || [];
    const filteredMeetings = meetings.filter((meeting) => meeting.job.id === jobId);
    return filteredMeetings.length ? filteredMeetings[0] : null;
  },
);

export const readChatMessage = createAsyncThunk(
  "chat/readChatMessage",
  async (chatId: string, { dispatch, getState }) => {
    await readChat(chatId);
    const { chat } = getState() as { chat: ChatState };
    const chatInList = chat.list.some((item) => item.id === chatId);
    if (!chatInList) {
      dispatch(getChatStats());
    }
    return chatId;
  },
);

export const getChats = createAsyncThunk(
  "chat/getChats",
  async (
    {
      searchQuery = "",
      organizationId = null,
      userId = null,
      append = false,
    }: {
      searchQuery?: string;
      organizationId?: string | null;
      userId?: string | null;
      append?: boolean;
    },
    { getState },
  ) => {
    const { chat } = getState() as { chat: ChatState };
    const { list, skip = 0, total } = chat;
    if (list.length === 0 || total > skip) {
      const response = await axiosInstance.get<V2Response<{ chats: Chat[]; total: number }>>(
        `${
          organizationId ? `organizations/${organizationId}` : `users/${userId}`
        }/chats?skip=${skip}&limit=20&q=${searchQuery}`,
      );
      return { data: response.data.result, append: append };
    }
  },
);

export const getUnreadChats = createAsyncThunk(
  "chat/getUnreadChats",
  async (
    {
      organizationId,
      userId,
      searchQuery = "",
      skip = 0,
    }: { organizationId: string; skip?: number; userId?: string; searchQuery?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosInstance.get<V2Response<{ chats: Chat[]; total: number }>>(
        `${
          organizationId ? `organizations/${organizationId}` : `users/${userId}`
        }/chats?status=unread&&limit=100&&skip=0&&q=${searchQuery}`,
      );
      return response.data.result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch unread chats");
    }
  },
);

export const getChatStats = createAsyncThunk("chat/getChatStats", async () => {
  const response = await axiosInstance.get<V2Response<{ unread_message_count: number }>>("chats/stats");
  return response.data.result;
});

export const getUserChats = createAsyncThunk(
  "chat/getUserChats",
  async ({ q = "", userId = null }: { q?: string; userId?: string | null }, { getState }) => {
    const { chat } = getState() as { chat: ChatState };
    const { list, skip = 0, total } = chat;

    if (list.length === 0 || total > skip) {
      const response = await axiosInstance.get<V2Response<{ chats: Chat[]; total: number }>>(`users/${userId}/chats?skip=${skip}&limit=20&q=${q}`);
      return { data: response.data.result, append: skip > 0 };
    }
  },
);

export const getOrganizationChats = createAsyncThunk(
  "chat/getOrganizationChats",
  async (
    { q = "", organizationId = null }: { q?: string; organizationId?: string },
    { getState },
  ) => {
    const { chat } = getState() as { chat: ChatState };
    const { list, skip = 0, total } = chat;

    if (list.length === 0 || total > skip) {
      const response = await axiosInstance.get<V2Response<{ chats: Chat[]; total: number }>>(
        `organizations/${organizationId}/chats?skip=${skip}&limit=20&q=${q}`,
      );
      return { data: response.data.result, append: skip > 0 };
    }
  },
);

export const updateChats = createAsyncThunk(
  "chat/updateChats",
  async ({
    chatToUpdate,
    selectedChat,
    currentUserType,
  }: {
    chatToUpdate: any;
    selectedChat: string;
    currentUserType: string;
  }) => {
    return { chatToUpdate, selectedChat, currentUserType };
  },
);

export const resetChatSkip = createAsyncThunk("chat/resetChatSkip", async (_, { dispatch }) => {
  dispatch(chatSlice.actions.setSkip(0));
});

export const clearChats = createAsyncThunk(
  "notification/clearNotifications",
  async (_, { dispatch }) => {
    dispatch(chatSlice.actions.resetChats());
  },
);

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    resetChats(state) {
      state.list = initialState.list;
      state.meeting = initialState.meeting;
      state.skip = initialState.skip;
      state.total = initialState.total;
      state.unread = initialState.unread;
    },
    setSkip(state, action: PayloadAction<number>) {
      state.skip = action.payload;
    },
    updateChatEnded(state, action: PayloadAction<{
      organization_id: string;
      job_id: string;
      jobseeker_id: string;
      ended: boolean
    }>) {
      const { organization_id, job_id, jobseeker_id, ended } = action.payload;
      const chatIndex = state.list.findIndex((chat) =>
        chat.organization?.id === organization_id &&
        chat.job?.id === job_id &&
        chat.jobseeker?.id === jobseeker_id
      );
      if (chatIndex !== -1) {
        state.list[chatIndex].ended = ended;
      }
    },
    updateChatListItem(
      state,
      action: PayloadAction<{
        chatId: string;
        lastMessage: any;
        incrementUnread?: boolean;
        resetUnread?: boolean;
        timestamp?: string;
        moveToTop?: boolean;
      }>,
    ) {
      const {
        chatId,
        lastMessage,
        incrementUnread = false,
        resetUnread = false,
        timestamp,
        moveToTop = true,
      } = action.payload;
      const chatIndex = state.list.findIndex((chat) => chat.id === chatId);

      if (chatIndex !== -1) {
        const chat = { ...state.list[chatIndex] };
        const previousUnreadCount = chat.unread_count || 0;

        chat.last_message = lastMessage;

        if (timestamp) {
          chat.created_at = timestamp;
        }

        if (resetUnread) {
          chat.unread_count = 0;
          state.unread = Math.max(0, (state.unread || 0) - previousUnreadCount);
        } else if (incrementUnread) {
          chat.unread_count = previousUnreadCount + 1;
          state.unread = (state.unread || 0) + 1;
        }

        const newList = [...state.list];
        newList.splice(chatIndex, 1);

        if (moveToTop) {
          newList.unshift(chat);
        } else {
          newList.splice(chatIndex, 0, chat);
        }

        state.list = newList;
      } else if (incrementUnread) {
        state.unread = (state.unread || 0) + 1;
      }
    },
    decrementUnreadCount(state, action: PayloadAction<{ chatId: string; amount?: number }>) {
      const { chatId, amount = 1 } = action.payload;
      const chatIndex = state.list.findIndex((chat) => chat.id === chatId);

      if (chatIndex !== -1) {
        const currentUnread = state.list[chatIndex].unread_count || 0;
        const decrementAmount = Math.min(amount, currentUnread);

        state.list[chatIndex].unread_count = Math.max(0, currentUnread - decrementAmount);
        state.unread = Math.max(0, (state.unread || 0) - decrementAmount);
      }
    },
    resetChatUnreadCount(state, action: PayloadAction<{ chatId: string }>) {
      const { chatId } = action.payload;
      const chatIndex = state.list.findIndex((chat) => chat.id === chatId);

      if (chatIndex !== -1) {
        const previousUnreadCount = state.list[chatIndex].unread_count || 0;

        state.list[chatIndex].unread_count = 0;

        state.unread = Math.max(0, (state.unread || 0) - previousUnreadCount);
      }
    },
    markUserAsDeleted(
      state,
      action: PayloadAction<{ userId: string; userType: "jobseeker" | "organization" }>,
    ) {
      const { userId, userType } = action.payload;

      // Update all chats where the deleted user is involved
      state.list.forEach((chat) => {
        if (userType === "jobseeker" && chat.jobseeker?.id === userId) {
          chat.jobseeker.deleted = true;
        } else if (userType === "organization" && chat.organization?.id === userId) {
          chat.organization.deleted = true;
        }
      });

      // Update unread_list as well
      state.unread_list.forEach((chat) => {
        if (userType === "jobseeker" && chat.jobseeker?.id === userId) {
          chat.jobseeker.deleted = true;
        } else if (userType === "organization" && chat.organization?.id === userId) {
          chat.organization.deleted = true;
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChatMeeting.fulfilled, (state, action: PayloadAction<any>) => {
        state.meeting = action.payload;
      })
      .addCase(readChatMessage.fulfilled, (state, action: PayloadAction<string>) => {
        const chatId = action.payload;

        // Update main chat list
        const index = state.list.findIndex((item) => item.id === chatId);
        if (index !== -1) {
          const unreadCount = state.list[index].unread_count || 0;
          state.list[index].unread_count = 0;
          state.unread = Math.max(0, state.unread - unreadCount);
        }

        // Update unread_list without removing the chat
        const unreadIndex = state.unread_list.findIndex((item) => item.id === chatId);
        if (unreadIndex !== -1) {
          state.unread_list[unreadIndex].unread_count = 0;
        }
      })
      .addCase(
        getChats.fulfilled,
        (state, action: PayloadAction<{ data: any; append: boolean }>) => {
          if (action.payload) {
            const newChats = action.payload.data?.chats || [];
            if (action.payload.append) {
              const existingChatIds = new Set(state.list.map((chat) => chat.id));
              const mergedChats = [
                ...state.list,
                ...newChats.filter((chat) => !existingChatIds.has(chat.id)),
              ];

              state.list = mergedChats;
              state.skip = mergedChats.length;
            } else {
              state.list = newChats;
              state.skip = newChats.length;
            }
            state.total = action.payload.data?.total || 0;
          }
        },
      )
      .addCase(getUnreadChats.fulfilled, (state, action: PayloadAction<any>) => {
        state.unread_list = action.payload.chats || [];
      })
      .addCase(getChatStats.fulfilled, (state, action: PayloadAction<{ unread_message_count: number }>) => {
        state.unread = action.payload?.unread_message_count || 0;
      })
      .addCase(
        getUserChats.fulfilled,
        (state, action: PayloadAction<{ data: any; append: boolean }>) => {
          if (action.payload.append) {
            state.list = [...state.list, ...action.payload.data.chats];
          } else {
            state.list = action.payload.data.chats;
          }
          state.total = action.payload.data.total;
          state.skip += 20;

          // const calculatedUnread = state.list.reduce((sum, chat) => {
          //   return sum + (chat.unread_count || 0);
          // }, 0);
          // state.unread = calculatedUnread;
        },
      )
      .addCase(
        getOrganizationChats.fulfilled,
        (state, action: PayloadAction<{ data: any; append: boolean }>) => {
          if (action.payload.append) {
            state.list = [...state.list, ...action.payload.data.chats];
          } else {
            state.list = action.payload.data.chats;
          }
          state.total = action.payload.data.total;
          state.skip += 20;
        },
      )
      .addCase(
        updateChats.fulfilled,
        (
          state,
          action: PayloadAction<{
            chatToUpdate: any;
            selectedChat: string;
            currentUserType: string;
          }>,
        ) => {
          const { list, unread } = state;
          const { chatToUpdate, currentUserType } = action.payload;
          let newList = [...list];
          let updatedUnread = unread;

          const index = list.findIndex((item) => item.id === chatToUpdate.chat_id);
          if (index !== -1) {
            const chat = { ...list[index] };

            chat.last_message = {
              ...chat.last_message,
              sender_id: chatToUpdate.sender_id,
              text: chatToUpdate.text,
            };

            if (chatToUpdate.status === "unread" && chatToUpdate.sender_ref !== currentUserType) {
              updatedUnread += 1;
              chat.unread_count = (chat.unread_count || 0) + 1;
            }

            newList[index] = chat;
          }

          state.list = newList;
          state.unread = updatedUnread;
        },
      );
  },
});

export const {
  resetChats,
  setSkip,
  updateChatEnded,
  updateChatListItem,
  decrementUnreadCount,
  resetChatUnreadCount,
  markUserAsDeleted,
} = chatSlice.actions;
export default chatSlice.reducer;
